import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { User, Mail, Phone, Calendar, Clock, Save, X, AlertCircle, CheckCircle, Link2, Copy } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API_URL } from '../config/api';

const API = API_URL;

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const TeamMemberProfile = ({ teamMemberId, teamMemberData, onUpdate }) => {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    doubleDeckerCertified: false,
    recurringAvailability: [],
    unavailableDates: []
  });
  const [editMode, setEditMode] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showAvailabilityDialog, setShowAvailabilityDialog] = useState(false);
  const [showUnavailableDatesDialog, setShowUnavailableDatesDialog] = useState(false);
  const [selectedUnavailableDates, setSelectedUnavailableDates] = useState([]);

  useEffect(() => {
    if (teamMemberData) {
      setProfile({
        name: teamMemberData.name || '',
        email: teamMemberData.email || '',
        phone: teamMemberData.phone || '',
        doubleDeckerCertified: teamMemberData.doubleDeckerCertified || false,
        recurringAvailability: teamMemberData.recurringAvailability || [],
        unavailableDates: teamMemberData.unavailableDates || []
      });
      setSelectedUnavailableDates(
        (teamMemberData.unavailableDates || []).map(d => new Date(d))
      );
    }
    fetchPendingRequests();
  }, [teamMemberData]);

  const fetchPendingRequests = async () => {
    try {
      const response = await axios.get(`${API}/change-requests/teamMember/${teamMemberId}`);
      setPendingRequests(response.data.filter(r => r.status === 'pending'));
    } catch (error) {
      console.error("Error fetching pending requests:", error);
    }
  };

  const handleSubmitChanges = async () => {
    try {
      // Prepare changes object
      const changes = {
        email: profile.email,
        phone: profile.phone,
        recurringAvailability: profile.recurringAvailability,
        unavailableDates: profile.unavailableDates
      };

      // Submit change request
      await axios.post(`${API}/change-requests`, {
        teamMemberId: teamMemberId,
        teamMemberName: teamMemberData.name,
        requestType: 'profile',
        changes: changes
      });

      toast.success("Profile changes submitted for admin approval!");
      setEditMode(false);
      fetchPendingRequests();
    } catch (error) {
      console.error("Error submitting changes:", error);
      toast.error("Failed to submit changes");
    }
  };

  const handleAddAvailability = (day) => {
    const existing = profile.recurringAvailability.find(a => a.day === day);
    if (!existing) {
      setProfile({
        ...profile,
        recurringAvailability: [
          ...profile.recurringAvailability,
          { day, startTime: '09:00', endTime: '17:00' }
        ]
      });
    }
  };

  const handleRemoveAvailability = (day) => {
    setProfile({
      ...profile,
      recurringAvailability: profile.recurringAvailability.filter(a => a.day !== day)
    });
  };

  const handleUpdateAvailability = (day, field, value) => {
    setProfile({
      ...profile,
      recurringAvailability: profile.recurringAvailability.map(a =>
        a.day === day ? { ...a, [field]: value } : a
      )
    });
  };

  const handleUnavailableDatesChange = (dates) => {
    setSelectedUnavailableDates(dates);
    setProfile({
      ...profile,
      unavailableDates: dates.map(d => d.toISOString().split('T')[0])
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Your Profile</h2>
          <p className="text-gray-600 mt-1">Manage your contact information and availability</p>
        </div>
        {!editMode ? (
          <Button onClick={() => setEditMode(true)}>
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditMode(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSubmitChanges}>
              <Save className="w-4 h-4 mr-2" />
              Submit for Approval
            </Button>
          </div>
        )}
      </div>

      {/* Pending Requests Alert */}
      {pendingRequests.length > 0 && (
        <Card className="border-yellow-500 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-900">
                  {pendingRequests.length} Pending Change Request{pendingRequests.length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Your profile changes are awaiting admin approval
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input
              value={profile.name}
              disabled
              className="bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">Contact admin to change your name</p>
          </div>

          <div>
            <Label htmlFor="email">
              <Mail className="w-4 h-4 inline mr-1" />
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              disabled={!editMode}
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <Label htmlFor="phone">
              <Phone className="w-4 h-4 inline mr-1" />
              Phone
            </Label>
            <Input
              id="phone"
              type="tel"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              disabled={!editMode}
              placeholder="(555) 123-4567"
            />
          </div>
        </CardContent>
      </Card>

      {/* Recurring Availability */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Weekly Availability
            </CardTitle>
            {editMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAvailabilityDialog(true)}
              >
                Manage Schedule
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {profile.recurringAvailability.length > 0 ? (
            <div className="space-y-2">
              {DAYS_OF_WEEK.map(day => {
                const avail = profile.recurringAvailability.find(a => a.day === day);
                return (
                  <div key={day} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <span className="font-medium capitalize">{day}</span>
                    {avail ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {avail.startTime} - {avail.endTime}
                        </Badge>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Not available</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-6">
              No availability schedule set. Click "Manage Schedule" to add your available hours.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Unavailable Dates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Unavailable Dates
            </CardTitle>
            {editMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUnavailableDatesDialog(true)}
              >
                Mark Days Off
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {profile.unavailableDates.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {profile.unavailableDates.map((date, idx) => (
                <Badge key={idx} variant="destructive">
                  {formatDate(date)}
                  {editMode && (
                    <X
                      className="w-3 h-3 ml-1 cursor-pointer"
                      onClick={() => {
                        const newDates = selectedUnavailableDates.filter(
                          d => d.toISOString().split('T')[0] !== date
                        );
                        handleUnavailableDatesChange(newDates);
                      }}
                    />
                  )}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-6">
              No unavailable dates marked. You're available all days!
            </p>
          )}
        </CardContent>
      </Card>

      {/* Personalized Booking Link */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Link2 className="w-5 h-5" />
            Your Booking Link
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-700">
            Share this link with customers via text, email, or social media. 
            They'll see "Book with {profile.name}" and you'll be automatically selected.
          </p>
          
          <div>
            <Label className="text-xs font-medium text-gray-700">Your Personal Link</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={`${window.location.origin}/widget-v2.html?teamMemberId=${teamMemberId}&teamMemberName=${encodeURIComponent(profile.name)}`}
                readOnly
                className="bg-white font-mono text-xs"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/widget-v2.html?teamMemberId=${teamMemberId}&teamMemberName=${encodeURIComponent(profile.name)}`);
                  toast.success("Link copied! Ready to share.");
                }}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 border border-blue-200">
            <p className="text-xs font-semibold text-blue-900 mb-2">💡 How to use:</p>
            <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
              <li>Copy and paste this link in a text message or email</li>
              <li>Customers will see they're booking with you specifically</li>
              <li>Bookings appear in your dashboard for confirmation</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Availability Dialog */}
      <Dialog open={showAvailabilityDialog} onOpenChange={setShowAvailabilityDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Weekly Availability</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {DAYS_OF_WEEK.map(day => {
              const avail = profile.recurringAvailability.find(a => a.day === day);
              return (
                <div key={day} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold capitalize">{day}</span>
                    {avail ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAvailability(day)}
                      >
                        Remove
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddAvailability(day)}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                  {avail && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Start Time</Label>
                        <Input
                          type="time"
                          value={avail.startTime}
                          onChange={(e) => handleUpdateAvailability(day, 'startTime', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">End Time</Label>
                        <Input
                          type="time"
                          value={avail.endTime}
                          onChange={(e) => handleUpdateAvailability(day, 'endTime', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setShowAvailabilityDialog(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unavailable Dates Dialog */}
      <Dialog open={showUnavailableDatesDialog} onOpenChange={setShowUnavailableDatesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Unavailable Dates</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-4">
              Select the dates you won't be available for trips
            </p>
            <CalendarComponent
              mode="multiple"
              selected={selectedUnavailableDates}
              onSelect={handleUnavailableDatesChange}
              className="rounded-md border"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowUnavailableDatesDialog(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamMemberProfile;
